import React, { useState, useMemo, useEffect, useRef, forwardRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Box, type BoxProps } from '../Box'
import { getHostNode } from '../../../utils'

export type DropdownContentProps = BoxProps & {
  target: HTMLElement | null,
  show?: boolean,
  position?: 'left' | 'right' | 'top' | 'bottom',
  align?: 'start' | 'center' | 'end',
  offsetY?: string | number | null,
  offsetX?: string | number | null,
  style?: React.CSSProperties,
  zIndex?: number | string | null,
  children: React.ReactNode,
  onClickOutside?: (e: MouseEvent) => void
}

type Rect = {
  top: number,
  left: number,
  width: number,
  height: number
}

const DEFAULT_RECT = { top: 0, left: 0, width: 0, height: 0 }

export const DropdownContent = forwardRef(function DropdownContent ({
  children,
  style,
  show = false,
  position = 'bottom',
  align = 'start',
  offsetX = 0,
  offsetY = 0,
  target,
  width,
  height,
  zIndex = '1000',
  className,
  onClickOutside,
  ...props
} : DropdownContentProps, ref) {

  // Get ref

  const root = useRef<HTMLElement | null>(null)
  const host = useRef<HTMLElement | null>(null)

  const getRootRef = (node: HTMLElement | null) => {
    try {
      root.current = node
      host.current = getHostNode(node as HTMLSpanElement)
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    } catch {}
  }

  // Rects

  const [reset, setReset] = useState<number>(0)

  const windowRect = useMemo<Rect>(() => ({
    top: 0,
    left: 0,
    width: window?.innerWidth || 0,
    height: window?.innerHeight || 0
  }), [reset])

  const targetRect = useMemo<Rect>(() => {
    const box = target?.getBoundingClientRect() ?? DEFAULT_RECT
    return {
      top: box.top,
      left: box.left,
      width: box.width,
      height: box.height
    }
  }, [target, reset])

  const contentRect = useMemo<Rect>(() => ({
    top: 0,
    left: 0,
    width: Number(width) || root.current?.scrollWidth || 0,
    height: Number(height) || root.current?.scrollHeight || 0
  }), [root, reset])

  const lazyShow = useRef<boolean>(show)

  // Content position

  const contentPosition = useMemo<Rect>(() => {
    if (!show) return DEFAULT_RECT

    const obj: Rect = { ...DEFAULT_RECT }
    const _offsetX = Number(offsetX) || 0
    const _offsetY = Number(offsetY) || 0

    // Define position values

    if (['top', 'bottom'].includes(position)) {
      if (align === 'start') obj.left = targetRect.left + _offsetX
      else if (align === 'center') obj.left = targetRect.left + targetRect.width / 2 - contentRect.width / 2 + _offsetX
      else if (align === 'end') obj.left = targetRect.left + targetRect.width - contentRect.width - _offsetX
      if (position === 'top') obj.top = targetRect.top - contentRect.height - _offsetY
      else if (position === 'bottom') obj.top = targetRect.top + targetRect.height + _offsetY
    } else {
      if (align === 'start') obj.top = targetRect.top + _offsetY
      else if (align === 'center') obj.top = targetRect.top + targetRect.height / 2 - contentRect.height / 2 + _offsetY
      else if (align === 'end') obj.top = targetRect.top + targetRect.height - contentRect.height - _offsetY
      if (position === 'left') obj.left = targetRect.left - contentRect.width - _offsetX
      else if (position === 'right') obj.left = targetRect.left + targetRect.width + _offsetX
    }

    // Normalize with window rect

    obj.top = Math.max(0, Math.min(windowRect.height - contentRect.height, obj.top))
    obj.left = Math.max(0, Math.min(windowRect.width - contentRect.width, obj.left))
    obj.width = Math.min(windowRect.width, contentRect.width)
    obj.height = Math.min(windowRect.height, contentRect.height)

    return obj
  }, [windowRect, targetRect, contentRect, show, position, align, offsetX, offsetY])

  // Refresh position

  useEffect(() => {
    const callback = () => setReset(reset + 1)
    window.addEventListener('resize', callback)
    return () => window.removeEventListener('resize', callback)
  }, [reset])

  useEffect(() => {
    show && window.requestAnimationFrame(() => setReset(reset + 1))
    if (!show) lazyShow.current = false
  }, [show])

  // Click outside

  const clickOutsideHandler = useCallback((e: MouseEvent) => {
    if (!show) return
    if (e.composedPath().includes(root.current as Node)) return
    lazyShow.current && onClickOutside?.(e)
    lazyShow.current = true
  }, [onClickOutside])

  useEffect(() => {
    window.addEventListener('click', clickOutsideHandler)
    return () => window.removeEventListener('click', clickOutsideHandler)
  }, [clickOutsideHandler])

  const element = (
    <Box
      ref={getRootRef}
      className={['topbar-dropwdown-menu', { show }, className]}
      style={{ ...style, ...contentPosition, zIndex }}
      {...props}
      minWidth={null}
      maxWidth={null}
      minHeight={null}
      maxHeight={null}
    >
      {children}
    </Box>
  )

  if (host.current) return createPortal(element, host.current)
  return element
})